/**
 * Departments — first page migrated from mockData to live svdy-platform API.
 *
 * Wave 3.1 reference implementation. The other 12 Organization pages
 * should follow this pattern:
 *
 *   1. Drop the local `types.ts` / `mockData.ts`. Use `Schema<'FooResponse'>`
 *      from `src/api` as the source of truth; stop maintaining a parallel
 *      display model.
 *   2. Replace local useState([...initialFoo]) with a fetch effect that
 *      calls `fooApi.list()` and sets state + loading + error.
 *   3. Keep the Create / Update / Delete handlers; map UI → API by
 *      typing the payload as `Schema<'FooCreate'>` etc.
 *   4. Columns that don't resolve 1:1 (here: HOD, Location — which are
 *      FK IDs on the backend but names in the UI) render as '-' for
 *      now; a later follow-up will join against the referenced tables
 *      or the backend will eager-load.
 *
 * NOT in scope for this migration:
 *   - Manager / location / branch name resolution (awaiting a join step
 *     or a richer DepartmentResponse).
 *   - CSV import; the existing button was already a placeholder.
 *   - Transfer Workers; belongs to a cross-table flow.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import AddIcon from '@mui/icons-material/Add';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CodeCell } from '../../../components/organization/CodeCell';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import type { ImportBatchSummary } from '../../../api/organization';
import { departmentImportApi } from '../../../api/organization';
import { ImportDialog } from '../../../components/import/ImportDialog';
import { CodeField, isCodeValid } from '../../../components/organization/CodeField';
import { ConfirmDialog } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';
import { fmtLocalDateTime } from '../../../utils/datetime';

type Department = Schema<'DepartmentResponse'>;
type DepartmentCreate = Schema<'DepartmentCreate'>;
type DepartmentUpdate = Schema<'DepartmentUpdate'>;

// Form state model: partial backend shape + one client-side-only bool for
// the "Active Status" checkbox which maps to `status` on save.
type FormState = Partial<Department> & { _isActive?: boolean };

const ACTIVE_STATUS = 'active';
const INACTIVE_STATUS = 'inactive';

function isActive(d: Department): boolean {
  return (d.status ?? ACTIVE_STATUS) === ACTIVE_STATUS;
}

const Departments: React.FC = () => {
  const { t } = useTranslation();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [workers, setWorkers] = useState<Schema<'WorkerResponse'>[]>([]);
  const [locations, setLocations] = useState<Schema<'LocationResponse'>[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [openFormDialog, setOpenFormDialog] = useState<boolean>(false);
  const [importOpen, setImportOpen] = useState<boolean>(false);
  const [lastImport, setLastImport] = useState<ImportBatchSummary | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [currentDepartment, setCurrentDepartment] =
    useState<FormState | null>(null);
  const [departmentToDelete, setDepartmentToDelete] =
    useState<Department | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');

  // Row kebab menu — hosts the cross-module shortcuts to People +
  // Attendance that the user called out as missing.
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<Department | null>(null);
  const navigate = useNavigate();

  const openRowMenu = (target: Department, anchor: HTMLElement) => {
    setMenuTarget(target);
    setMenuAnchor(anchor);
  };
  const closeRowMenu = () => setMenuAnchor(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const [depts, workers, locs] = await Promise.all([
        organizationApi.departmentsApi.list(),
        organizationApi.workersApi.list(),
        organizationApi.locationsApi.list(),
      ]);
      setDepartments(depts);
      setWorkers(workers);
      setLocations(locs);
      setError(null);
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:departments.toast.loadFailed');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  /**
   * The last-import line of §11.4. Its own fetch rather than part of
   * `fetchDepartments`, because a failure here must not blank the departments
   * table — the history is context, not the page's subject.
   */
  const fetchLastImport = useCallback(async () => {
    try {
      const batches = await departmentImportApi.batches(1);
      setLastImport(batches[0] ?? null);
    } catch {
      setLastImport(null);
    }
  }, []);

  useEffect(() => {
    fetchLastImport();
  }, [fetchLastImport]);

  // FK lookup maps — resolve manager_id / location_id to readable names.
  const managerName = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of workers) {
      m.set(e.id, `${e.first_name} ${e.last_name}`.trim() || e.worker_code);
    }
    return m;
  }, [workers]);

  const locationName = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of locations) m.set(l.id, l.name);
    return m;
  }, [locations]);

  // -------------------------------------------------------------------------
  // Summary KPIs
  // -------------------------------------------------------------------------

  const summaryStats = useMemo(() => {
    const total = departments.length;
    const active = departments.filter(isActive).length;
    const noManager = departments.filter((d) => !d.manager_id).length;
    const totalWorkers = departments.reduce(
      (sum, d) => sum + (d.worker_count ?? 0),
      0,
    );
    return { total, active, noManager, totalWorkers };
  }, [departments]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:departments.stats.total'),
        description: t('org:departments.stats.totalDescription'),
        avatar: {
          icon: <ApartmentOutlinedIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'active',
        value: summaryStats.active,
        title: t('org:departments.stats.active'),
        description: t('org:departments.stats.activeDescription'),
        avatar: {
          icon: <CheckCircleOutlineIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'nomanager',
        value: summaryStats.noManager,
        title: t('org:departments.stats.noManager'),
        description: t('org:departments.stats.noManagerDescription'),
        avatar: {
          icon: <WarningAmberIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
      {
        id: 'workers',
        value: summaryStats.totalWorkers,
        title: t('org:departments.stats.totalHeadcount'),
        description: t('org:departments.stats.totalHeadcountDescription'),
        avatar: {
          icon: <PeopleAltOutlinedIcon />,
          bgcolor: 'info.light',
          color: 'info.main',
        },
      },
    ],
    [summaryStats, t],
  );

  // -------------------------------------------------------------------------
  // Filtering + pagination (client-side; list endpoint returns all rows)
  // -------------------------------------------------------------------------

  const filteredDepartments = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return departments;
    return departments.filter(
      (d) =>
        d.name.toLowerCase().includes(needle) ||
        (d.code ?? '').toLowerCase().includes(needle) ||
        (d.description ?? '').toLowerCase().includes(needle),
    );
  }, [departments, filterText]);

  const paginatedDepartments = useMemo(
    () =>
      filteredDepartments.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredDepartments, page, rowsPerPage],
  );

  // -------------------------------------------------------------------------
  // Parent-department options: offer existing departments so the user can
  // nest under an already-created one. Self-parenting is prevented when
  // editing by excluding the current node.
  // -------------------------------------------------------------------------

  const parentDepartmentOptions = useMemo(() => {
    const editingId = currentDepartment?.id;
    return departments
      .filter((d) => d.id !== editingId)
      .map((d) => ({ id: d.id, name: d.name }));
  }, [departments, currentDepartment?.id]);

  // -------------------------------------------------------------------------
  // Form handlers
  // -------------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!currentDepartment) return false;
    const errs: Record<string, string> = {};
    if (!currentDepartment.name?.trim()) {
      errs.name = t('org:departments.fields.nameRequired');
    }
    // ADR-043 D6. The field already shows the specific rule under itself, so this
    // only has to stop the submit — the server refuses it too, and would return
    // `invalid_org_code`, but making the admin wait for a round trip to learn
    // about a space in a code is a worse experience for the same outcome.
    if (!isCodeValid(currentDepartment.code || '')) {
      errs.code = t('org:shared.codeField.label');
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddDialog = () => {
    setCurrentDepartment({ _isActive: true });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleOpenEditDialog = (dept: Department) => {
    setCurrentDepartment({ ...dept, _isActive: isActive(dept) });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrentDepartment(null);
    setFormErrors({});
  };

  const handleSaveDepartment = async () => {
    if (!validateForm() || !currentDepartment) return;
    setSaving(true);
    try {
      if (currentDepartment.id) {
        const update: DepartmentUpdate = {
          name: currentDepartment.name ?? null,
          code: currentDepartment.code ?? null,
          description: currentDepartment.description ?? null,
          parent_id: currentDepartment.parent_id ?? null,
          manager_id: currentDepartment.manager_id ?? null,
          location_id: currentDepartment.location_id ?? null,
          status: currentDepartment._isActive
            ? ACTIVE_STATUS
            : INACTIVE_STATUS,
        };
        await organizationApi.departmentsApi.update(
          currentDepartment.id,
          update,
        );
        setToast(t('org:departments.toast.updated', { name: currentDepartment.name }));
      } else {
        const create: DepartmentCreate = {
          name: currentDepartment.name!,
          code: currentDepartment.code ?? null,
          description: currentDepartment.description ?? null,
          parent_id: currentDepartment.parent_id ?? null,
          manager_id: currentDepartment.manager_id ?? null,
          location_id: currentDepartment.location_id ?? null,
        };
        await organizationApi.departmentsApi.create(create);
        setToast(t('org:departments.toast.created', { name: create.name }));
      }
      handleCloseFormDialog();
      await fetchDepartments();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:departments.toast.saveFailed');
      setFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (dept: Department) => {
    setDepartmentToDelete(dept);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDepartmentToDelete(null);
  };

  const handleDeleteDepartment = async () => {
    if (!departmentToDelete) return;
    setSaving(true);
    try {
      await organizationApi.departmentsApi.delete(departmentToDelete.id);
      setSelectedDepartments((prev) =>
        prev.filter((id) => id !== departmentToDelete.id),
      );
      // Dialog auto-closes via its onClose on successful confirm; we
      // still need to reset local state here so the row selection and
      // "to-delete" marker clear together.
      handleCloseDeleteDialog();
      setToast(t('org:departments.toast.deleted', { name: departmentToDelete.name }));
      await fetchDepartments();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:departments.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for a retry; we
      // close manually on the success branch above.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDepartments.length === 0) {
      setToast(t('org:departments.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedDepartments) {
      try {
        await organizationApi.departmentsApi.delete(id);
      } catch (e) {
        failures.push(id);
      }
    }
    setSelectedDepartments([]);
    await fetchDepartments();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:departments.toast.bulkDeleted', { count: selectedDepartments.length }));
    } else {
      setToast(
        t('org:departments.toast.bulkDeletedPartial', {
          ok: selectedDepartments.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setCurrentDepartment((prev) =>
      prev
        ? {
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
          }
        : null,
    );
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setCurrentDepartment((prev) =>
      prev ? { ...prev, [name]: value === '' ? null : value } : null,
    );
  };

  // -------------------------------------------------------------------------
  // Row selection / pagination
  // -------------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedDepartments((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedDepartments.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilterText(event.target.value);
    setPage(0);
  };

  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      setSelectedDepartments(filteredDepartments.map((d) => d.id));
    } else {
      setSelectedDepartments([]);
    }
  };

  // -------------------------------------------------------------------------
  // Export — the IMPORT TEMPLATE, not a snapshot of the table.
  //
  // Two defects were fixed here at once (2026-08-09 audit):
  //
  //  1. it exported `paginatedDepartments` — the CURRENT PAGE ONLY, and said so in a
  //     comment, so it shipped known-incomplete. An export is what answers a records
  //     request; a file holding page 1 of 4 is worse than no file because it looks
  //     complete.
  //  2. it was a display snapshot, so it ALMOST round-tripped: it wrote `Manager` and
  //     `Location` as display names and a `worker_count` that is not importable, and
  //     an admin who edited it and re-uploaded would have every FK column fail. That
  //     is the exact trap the workers page hit in import slice 3.1, fixed there by
  //     making the backend template the single producer of the file.
  //
  // So departments now does what workers does: one producer, `_code` beside every
  // name, a BOM the old export did not write, and the current filter carried as ids.
  const handleExport = async () => {
    try {
      await departmentImportApi.downloadTemplate(
        false,
        filteredDepartments.map((d) => d.id),
      );
      setToast(t('org:departments.toast.exportDone', { count: filteredDepartments.length }));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.errorGeneric'));
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading && departments.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && departments.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchDepartments}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      {/* Title + actions */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            }}
          >
            {t('org:departments.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:departments.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchDepartments}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('org:departments.addButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkDelete}
            size="small"
            color="error"
            disabled={selectedDepartments.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              mr: 1,
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleExport}
            size="small"
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.2)',
              bgcolor: '#ffffff',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.35)',
                bgcolor: '#ffffff',
              },
              mr: 1,
            }}
          >
            {t('common:actions.export')}
          </Button>
          {/*
            OUTLINED, never contained: the page's one contained button stays
            "+ Add Department", which is what most visits want (§11.1). Paired
            with Export so the two read as one round trip.
          */}
          <Button
            variant="outlined"
            onClick={() => setImportOpen(true)}
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.2)',
              bgcolor: '#ffffff',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.35)',
                bgcolor: '#ffffff',
              },
            }}
          >
            {t('org:import.button')}
          </Button>
        </Box>
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      {/*
        §11.4 — "Last import: 42 departments, 2026-08-06 14:12, by Robin — Undo".
        A line, not a route: this is also how an admin answers "who changed this",
        and it belongs where the data is.
      */}
      {lastImport && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            lastImport.created_by_name
              ? 'org:import.entities.departments.historyLast'
              : 'org:import.entities.departments.historyLastNoUser',
            {
              count: lastImport.row_count,
              when: fmtLocalDateTime(lastImport.created_at),
              who: lastImport.created_by_name,
            },
          )}
          {lastImport.undone_at && (
            <Typography component="span" variant="body2" color="text.disabled">
              {' · '}
              {t('org:import.history.undoneAt', {
                when: fmtLocalDateTime(lastImport.undone_at),
              })}
            </Typography>
          )}
        </Typography>
      )}

      {/* Filter + pagination controls */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          variant="outlined"
          label={t('org:departments.filterLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: '400px', maxWidth: '100%', mr: 2 }}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredDepartments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '.MuiTablePagination-toolbar': { paddingLeft: '16px' },
            '.MuiTablePagination-displayedRows': { margin: 0 },
            '.MuiTablePagination-actions': { marginLeft: '8px' },
          }}
        />
      </Box>

      {filteredDepartments.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText
              ? t('org:departments.empty.noMatches')
              : t('org:departments.empty.noResults')}
          </Typography>
          {!filterText && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mt: 2 }}
            >
              {t('org:departments.addButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label={t('org:departments.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedDepartments.length > 0 &&
                      selectedDepartments.length < filteredDepartments.length
                    }
                    checked={
                      filteredDepartments.length > 0 &&
                      selectedDepartments.length ===
                        filteredDepartments.length
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-label': t('org:departments.selectAllAria') }}
                  />
                </TableCell>
                <TableCell>{t('org:departments.table.name')}</TableCell>
                <TableCell>{t('org:departments.table.code')}</TableCell>
                <TableCell>{t('org:departments.table.manager')}</TableCell>
                <TableCell>{t('org:departments.table.location')}</TableCell>
                <TableCell>{t('org:departments.table.workers')}</TableCell>
                <TableCell>{t('org:departments.table.status')}</TableCell>
                <TableCell align="center">{t('org:departments.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDepartments.map((dept) => {
                const isItemSelected = isSelected(dept.id);
                return (
                  <TableRow
                    hover
                    key={dept.id}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={(event) => handleSelectRowClick(event, dept.id)}
                        inputProps={{
                          'aria-labelledby': `department-checkbox-${dept.id}`,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={`department-checkbox-${dept.id}`}
                      scope="row"
                    >
                      {dept.name}
                    </TableCell>
                    <TableCell>
                      <CodeCell code={dept.code} codeSource={dept.code_source} />
                    </TableCell>
                    {/* Wave 3.1 follow-up done: resolve manager_id /
                         location_id to readable names via client-side
                         lookup maps (managerName / locationName) built
                         from a parallel fetch of workers + locations
                         on mount. If the backend later ships an
                         eager-loaded DepartmentResponse.manager_name /
                         location_name this client-side join becomes
                         redundant and can be deleted. */}
                    <TableCell>
                      {dept.manager_id
                        ? managerName.get(dept.manager_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {dept.location_id
                        ? locationName.get(dept.location_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>{dept.worker_count ?? 0}</TableCell>
                    <TableCell>{dept.status ?? ACTIVE_STATUS}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(dept);
                        }}
                        size="small"
                        aria-label={t('org:shared.table.edit')}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openRowMenu(dept, e.currentTarget);
                        }}
                        size="small"
                        aria-label={t('org:shared.table.more')}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, py: 2 }}>
          {currentDepartment?.id ? t('org:departments.editDialogTitle') : t('org:departments.addDialogTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {formErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors._global}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={t('org:departments.fields.name')}
            type="text"
            fullWidth
            variant="outlined"
            value={currentDepartment?.name || ''}
            onChange={handleFormChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mb: 2 }}
          />
          <CodeField
            value={currentDepartment?.code || ''}
            onChange={(code) =>
              setCurrentDepartment((prev) => (prev ? { ...prev, code } : prev))
            }
            codeSource={currentDepartment?.code_source}
          />
          <TextField
            margin="dense"
            name="description"
            label={t('org:departments.fields.description')}
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={currentDepartment?.description || ''}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentDepartment?._isActive ?? true}
                onChange={handleFormChange}
                name="_isActive"
                color="primary"
              />
            }
            label={t('org:departments.fields.active')}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="parent-department-label">
              {t('org:departments.fields.parent')}
            </InputLabel>
            <Select
              labelId="parent-department-label"
              name="parent_id"
              value={currentDepartment?.parent_id || ''}
              label={t('org:departments.fields.parent')}
              onChange={handleSelectChange}
            >
              <MenuItem value="">
                <em>{t('org:shared.form.none')}</em>
              </MenuItem>
              {parentDepartmentOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Manager — HOD of this department. Optional; picks any
               active worker (excluding anyone whose employment already
               sits inside a child department would be stricter, but a
               simple 'any worker' Autocomplete is fine at this scale). */}
          <Autocomplete
            options={workers}
            value={
              workers.find((e) => e.id === currentDepartment?.manager_id) ??
              null
            }
            getOptionLabel={(opt) =>
              typeof opt === 'string'
                ? opt
                : `${opt.first_name} ${opt.last_name} (${opt.worker_code})`
            }
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_event, v) =>
              setCurrentDepartment((prev) =>
                prev ? { ...prev, manager_id: v?.id ?? null } : prev,
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t('org:departments.fields.manager')}
                placeholder={t('org:departments.fields.managerSearch')}
              />
            )}
            sx={{ mt: 2 }}
          />

          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="location-label">
              {t('org:departments.fields.primaryLocation')}
            </InputLabel>
            <Select
              labelId="location-label"
              name="location_id"
              value={currentDepartment?.location_id || ''}
              label={t('org:departments.fields.primaryLocation')}
              onChange={handleSelectChange}
            >
              <MenuItem value="">
                <em>{t('org:shared.form.none')}</em>
              </MenuItem>
              {locations.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSaveDepartment}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : currentDepartment?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:departments.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row kebab menu — cross-module shortcuts to People +
          Attendance. Keeps the department as the pivot point so
          operators don't lose context when jumping modules. */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const id = menuTarget?.id;
            closeRowMenu();
            navigate(id ? `/organization/workers/active?department_id=${id}` : '/organization/workers/active');
          }}
        >
          <ListItemIcon><GroupOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:departments.menu.viewWorkers')}
            secondary={menuTarget ? t('org:departments.menu.viewWorkersHint', { count: menuTarget.worker_count ?? 0 }) : undefined}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const id = menuTarget?.id;
            closeRowMenu();
            navigate(id ? `/attendance/schedules/department?department_id=${id}` : '/attendance/schedules/department');
          }}
        >
          <ListItemIcon><EventNoteOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:departments.menu.schedules')}
            secondary={t('org:departments.menu.schedulesHint')}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const id = menuTarget?.id;
            closeRowMenu();
            navigate(id ? `/attendance/rules/department?department_id=${id}` : '/attendance/rules/department');
          }}
        >
          <ListItemIcon><RuleFolderOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:departments.menu.rules')}
            secondary={t('org:departments.menu.rulesHint')}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuTarget) handleOpenDeleteDialog(menuTarget);
            closeRowMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText
            primary={t('common:actions.delete')}
            secondary={t('org:departments.menu.deleteHint')}
          />
        </MenuItem>
      </Menu>

      {/* Delete confirmation — shared ConfirmDialog primitive so all
          Organization-module delete prompts look the same. */}
      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:departments.deleteTitle')}
        description={t('org:departments.deleteDescription')}
        subject={
          departmentToDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {departmentToDelete.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDeleteDepartment}
        onClose={handleCloseDeleteDialog}
      />

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApplied={() => {
          fetchDepartments();
          fetchLastImport();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Departments;
